import { DateFormat } from "@constants/format";
import { Text, View } from "@react-pdf/renderer";
import { format } from "date-fns";
import { isEmpty } from "lodash";
import { useMemo } from "react";
import { RenderPDFItemProps } from "@models/platform-analysis/entities/steps/summary";
import { PDFStyleConfigs } from "../../export-pdf/configs";
import { useTheme } from "@mui/material";
import { useAnalysisPDFStyles } from "@hooks/useAnalysisPDFStyle";

export const PDFSupplierInformation = ({ analysis }: RenderPDFItemProps) => {
  const { typographyStyle } = useAnalysisPDFStyles();
  const { palette } = useTheme();

  const {
    name,
    ownerName,
    adviserName,
    practiceName,
    supplierName,
    lastModifiedDate,
    serial,
  } = useMemo(() => {
    const {
      name,
      supplierName,
      ownerName,
      adviserName,
      practiceName,
      lastModifiedDate,
      serial,
    } = analysis.summary || {};

    return {
      name: name || "",
      supplierName: supplierName || "",
      ownerName: ownerName || "",
      adviserName: adviserName || "",
      practiceName: practiceName || "",
      lastModifiedDate: lastModifiedDate || new Date(),
      serial: serial || "",
    };
  }, [analysis.summary]);

  const isShowAdviserName = useMemo(() => {
    return !isEmpty(adviserName) && !isEmpty(practiceName);
  }, [adviserName, practiceName]);

  return (
    <View
      style={{
        justifyContent: "space-between",
        alignItems: "flex-start",
        flexDirection: "column",
        height: PDFStyleConfigs.FirstPageBodyHeight - PDFStyleConfigs.GapLarge,
      }}
    >
      <Text
        style={{
          ...typographyStyle.title,
          color: palette.common.white,
          fontSize: 24,
        }}
      >
        {name}
      </Text>

      <View style={{ gap: PDFStyleConfigs.GapMedium }}>
        {isShowAdviserName ? (
          <Text
            style={{
              ...typographyStyle.title,
              color: palette.common.white,
              fontWeight: "normal",
            }}
          >
            {`This analysis is for: `}
            <Text
              style={{
                ...typographyStyle.title,
                fontWeight: "bold",
                color: palette.common.white,
              }}
            >
              {adviserName}
            </Text>
            {` from `}
            <Text
              style={{
                ...typographyStyle.title,
                fontWeight: "bold",
                color: palette.common.white,
              }}
            >
              {practiceName}
            </Text>
          </Text>
        ) : (
          <Text
            style={{
              ...typographyStyle.title,
              color: palette.common.white,
              fontWeight: "normal",
            }}
          >
            {`This analysis is created for `}
            <Text
              style={{
                ...typographyStyle.title,
                fontWeight: "bold",
                color: palette.common.white,
              }}
            >
              {supplierName}
            </Text>
            {` internal use only`}
          </Text>
        )}

        <Text
          style={{
            ...typographyStyle.title,
            color: palette.common.white,
            fontWeight: "normal",
          }}
        >
          {`Created by: `}
          <Text style={typographyStyle.title}>{ownerName}</Text>
          {` of `}
          <Text style={typographyStyle.title}>{supplierName}</Text>
        </Text>
        <Text
          style={{
            ...typographyStyle.subtitle,
            color: palette.common.white,
            fontWeight: "normal",
          }}
        >
          {`${
            lastModifiedDate
              ? `Last edit: ${format(new Date(lastModifiedDate), DateFormat)}`
              : ""
          }  |  ${
            analysis.databaseVersion
              ? `Data valid: ${format(
                  new Date(analysis.databaseVersion),
                  DateFormat,
                )}`
              : ""
          } |  Serial: ${serial}`}
        </Text>
      </View>
    </View>
  );
};
